/**
 * SidebarGroup Component
 *
 * Collapsible navigation group with icon and children.
 * Shows chevron indicator when expanded.
 */

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'

export default function SidebarGroup({
  label,
  icon: Icon,
  isExpanded,
  isActive,
  children
}) {
  // Groups are auto-expanded when they have an active child
  const [isOpen, setIsOpen] = useState(isActive)

  // Auto-expand when active changes
  if (isActive && !isOpen) {
    setIsOpen(true)
  }

  const handleToggle = () => {
    if (isExpanded) {
      setIsOpen(!isOpen)
    }
  }

  return (
    <div className="relative">
      {/* Group Header */}
      <button
        onClick={handleToggle}
        className={cn(
          'w-full flex items-center gap-3 py-2.5 rounded-md',
          'transition-colors duration-150',
          'hover:bg-accent/50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isExpanded ? 'px-3 justify-start' : 'justify-center px-0',
          isActive && 'text-foreground font-medium'
        )}
        aria-expanded={isOpen}
        aria-label={label}
      >
        <Icon className="w-5 h-5 flex-shrink-0 text-muted-foreground" />

        {isExpanded && (
          <>
            <span className="flex-1 text-left text-sm truncate">{label}</span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-muted-foreground transition-transform duration-200',
                isOpen && 'rotate-180'
              )}
            />
          </>
        )}
      </button>

      {/* Children - only show when expanded and open */}
      {isExpanded && isOpen && (
        <div className="mt-1 space-y-0.5 pl-4">
          {children}
        </div>
      )}

      {/* Tooltip for collapsed state */}
      {!isExpanded && (
        <div
          className={cn(
            'absolute left-full top-1/2 -translate-y-1/2 ml-2',
            'px-2 py-1 rounded bg-popover border border-border shadow-md',
            'text-sm text-popover-foreground whitespace-nowrap',
            'opacity-0 pointer-events-none',
            'group-hover:opacity-100 group-hover:pointer-events-auto',
            'transition-opacity duration-150 z-50'
          )}
          role="tooltip"
        >
          {label}
        </div>
      )}
    </div>
  )
}
